/** Displays that behave as inline for plain-text extraction (no extra newline after). */
const INLINE_LIKE_DISPLAY = new Set([
  "inline",
  "inline-block",
  "inline-flex",
  "inline-grid",
  "inline-table",
  "contents",
  "ruby",
  "ruby-text",
]);

/**
 * Plain text from a contenteditable, preserving Shift+Enter and block boundaries.
 * innerText/textContent often drop or collapse line breaks from &lt;br&gt; / block nodes.
 */
export function serializeContentEditableToPlainText(root: HTMLElement): string {
  const pieces: string[] = [];

  const walk = (parent: HTMLElement) => {
    for (const node of Array.from(parent.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) {
        pieces.push((node as Text).data);
        continue;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) continue;

      const el = node as HTMLElement;
      if (el.tagName === "BR") {
        pieces.push("\n");
        continue;
      }

      const display = window.getComputedStyle(el).display;
      if (display === "contents") {
        walk(el);
        continue;
      }

      const isBlock = !INLINE_LIKE_DISPLAY.has(display);
      if (isBlock) {
        walk(el);
        pieces.push("\n");
      } else {
        walk(el);
      }
    }
  };

  walk(root);
  return pieces.join("").replace(/\u00a0/g, " ");
}

/** Browsers leave a lone &lt;br&gt; in empty contenteditables; remove before paste. */
export function clearContentEditablePlaceholder(root: HTMLElement): void {
  if (root.textContent?.trim()) return;

  const { childNodes } = root;
  if (childNodes.length === 1) {
    const only = childNodes[0];
    if (
      only.nodeType === Node.ELEMENT_NODE &&
      (only as HTMLElement).tagName === "BR"
    ) {
      root.innerHTML = "";
    }
  }
}

/** Build a DOM fragment from plain text using &lt;br&gt; for line breaks (no block wrappers). */
export function buildPlainTextFragment(text: string): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const lines = text.replace(/\r\n/g, "\n").split("\n");

  lines.forEach((line, index) => {
    if (index > 0) {
      fragment.appendChild(document.createElement("br"));
    }
    if (line.length > 0) {
      fragment.appendChild(document.createTextNode(line));
    }
  });

  return fragment;
}

/** Insert plain text at the current selection, replacing any selected content. */
export function insertPlainTextAtSelection(
  text: string,
  root: HTMLElement,
): void {
  if (!text) return;

  if (!serializeContentEditableToPlainText(root).trim()) {
    clearContentEditablePlaceholder(root);
  }

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);
  range.deleteContents();
  range.insertNode(buildPlainTextFragment(text));
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
}
